import { Session } from 'next-auth';

import { cmsService } from '@services/cms';
import { getAppConfig } from '@services/config';
import { CACHE_TAGS } from '@services/http/http';

import { sapicRepository } from '@repositories/sapic';

import { RootCategoryType } from '@app-types/api/categories';
import { SapicFundingMode } from '@app-types/api/sapic';
import { CategoryCodeInfo, CategoryListType, MobilePlanType, OffersByCategoryCode } from '@app-types/plan';
import { SimulatorCart } from '@app-types/simulator/virtual-cart';

import {
    PremiumSubCategories,
    ProPremiumSubCategories,
    ProSimoSubCategories,
    SimoSubCategories,
} from '@helpers/offer-wall';
import { mapApiPlanToMobilePlanType } from '@helpers/product';

import { PlanRange, PlanTypeId } from '@constants/plan';

import { apiHttpService } from './http';
import { createVirtualCart } from './simulator';

const appConfig = getAppConfig();

function orderPlansByCategories(categoryList: CategoryListType): CategoryListType {
    const order = [...SimoSubCategories, ...PremiumSubCategories, ...ProSimoSubCategories, ...ProPremiumSubCategories];
    return categoryList.sort((a, b) => {
        return order.indexOf(a?.code as PlanTypeId) - order.indexOf(b?.code as PlanTypeId);
    });
}

export async function getOfferWallData(
    categoriesCodes: string[],
    virtualCart: SimulatorCart = createVirtualCart(),
    session: Session | null = null,
    isWithPhoneDetails: boolean = false,
): Promise<CategoryListType> {
    try {
        const [sapiCategoryList, apiResponse, plansCms] = await Promise.all([
            getApiProductDetails(categoriesCodes),
            sapicRepository.getMobilePlans(
                virtualCart,
                {
                    withDetails: isWithPhoneDetails,
                    categories: [PlanTypeId.SIMO, PlanTypeId.PREMIUM],
                    fundingMode: SapicFundingMode.MIN_ONE_OFF,
                    limit: -1,
                },
                session || undefined,
            ),
            cmsService.getPlansCms(),
        ]);

        const categoryList = sapiCategoryList.categories.map((category) => {
            const productsList: MobilePlanType[] = [];
            category.products.forEach((plan) => {
                const planSapic = apiResponse.products?.find((p) => p.gencode === plan.gencode);
                if (planSapic) {
                    const planCms = plansCms.items?.find((cmsPlan) => cmsPlan.gencode === plan.gencode);
                    const planFull = mapApiPlanToMobilePlanType(plan, planSapic, undefined, planCms);
                    const smartChangeEquivalent = apiResponse.products.find(
                        (p) =>
                            p.dataEnvelope === planFull.dataEnvelope &&
                            p.gencode !== planFull.gencode &&
                            p.subTypeId !== planFull.subTypeId,
                    );
                    planFull.hasSmartChangeEquivalent = Boolean(smartChangeEquivalent);
                    if (smartChangeEquivalent) {
                        planFull.mirrorPlans = {
                            swappy: smartChangeEquivalent,
                        };
                    }
                    if (planFull.commercialRange === PlanRange.WITHOUT_MOBILE) {
                        const famMirrorPlan = apiResponse.products.find(
                            (p) =>
                                p.dataEnvelope === planFull.dataEnvelope &&
                                p.commercialRange === PlanRange.WITH_MOBILE &&
                                p.gencode !== planFull.gencode &&
                                p.subTypeId === planFull.subTypeId,
                        );
                        planFull.mirrorPlans = {
                            ...planFull.mirrorPlans,
                            fam: famMirrorPlan,
                        };
                    }
                    productsList.push(planFull);
                }
            });

            return {
                id: category.id,
                name: category.name,
                description: category.description,
                code: category.code,
                subtitle: category.subtitle,
                url: category.url,
                products: productsList,
                _links: {
                    detailCategory: {
                        href: category._links.detailCategory.href,
                    },
                },
            };
        });

        return orderPlansByCategories(categoryList);
    } catch (error) {
        console.error('Could not fetch product details', error);
        throw new Error(`Could not fetch product details`);
    }
}

export async function getApiProductDetails(
    url: string[],
    withProducts: boolean = true,
    withDetails: boolean = true,
): Promise<RootCategoryType> {
    return apiHttpService.get<RootCategoryType>(
        `${appConfig.b2r.url}/ventes/categories`,
        {
            codes: url.join(','),
            voirProduits: `${withProducts}`,
            avecDetails: `${withDetails}`,
        },
        {
            tags: [CACHE_TAGS.FAI],
            ttl: Number.parseInt(appConfig.cache.ttl.products),
        },
    );
}

export async function getFaiOffersByCategory(categoryCodes: string[]): Promise<{
    offersByCategoryCode: OffersByCategoryCode;
    gencodesByCategoryCode: Record<string, string[]>;
}> {
    const offersByCategory = await getApiProductDetails(categoryCodes, true, false).catch(() => ({
        categories: [],
    }));
    const offersByCategoryCode: OffersByCategoryCode = {};
    const gencodesByCategoryCode: Record<string, string[]> = {};

    offersByCategory.categories.forEach((cat) => {
        offersByCategoryCode[cat.code] = cat.products.reduce((uniqueProducts, product) => {
            const isInCategory = uniqueProducts.some(
                (p) =>
                    p.rangeNg === product.range_ng &&
                    p.commercialSeries === product.commercial_series &&
                    p.technology === product.technology &&
                    p.commercialPartner === product.commercial_partner,
            );
            if (!isInCategory) {
                uniqueProducts.push({
                    rangeNg: product.range_ng ?? 'xgbox',
                    commercialSeries: (product.commercial_series ?? product.classification)!,
                    technology: product.technology!,
                    shortDescription: product.short_description,
                    range: (product.range ?? product.classification)!,
                    categories: product.categories ?? [],
                    commercialPartner: product.commercial_partner,
                });
            }

            return uniqueProducts;
        }, [] as CategoryCodeInfo[]);

        gencodesByCategoryCode[cat.code] = cat.products.map((p) => p.gencode);
    });

    return { offersByCategoryCode, gencodesByCategoryCode };
}
